import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';

const isTouchDevice = () =>
  typeof window !== 'undefined' && (('ontouchstart' in window) || (navigator.maxTouchPoints || 0) > 0);

/**
 * RefreshControl — app-wide refresh & update from any screen.
 *  • Touch devices: pull the page down (from the very top) to refresh.
 *  • Non-touch (Windows / desktop browser): a floating refresh button.
 * Reloading picks up any newly-deployed build (index.html is no-store +
 * the load-time / poller version checks self-heal to the latest version).
 */
export default function RefreshControl() {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const active = useRef(false);
  const isTouch = isTouchDevice();
  const location = useLocation();
  // Pull-to-refresh conflicts with the chat/message scroll-up (which loads
  // older history): a swipe-down at the top of the message list was reloading
  // the app back to the chat home screen. Disable PTR on chat/message screens.
  // Also off on Navigation: the map/panels use drag gestures and the page has its own ↻ button.
  const ptrDisabled = /\/chat(\/|$)/.test(location.pathname || '') || /\/navigation(\/|$)/.test(location.pathname || '');
  const THRESHOLD = 70;
  const MAX = 110;

  const doRefresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    setTimeout(() => window.location.reload(), 500);
  };

  useEffect(() => {
    if (!isTouch || ptrDisabled) return;
    const atTop = () => (window.scrollY || document.documentElement.scrollTop || 0) <= 0;
    const onStart = (e) => {
      if (!atTop()) { active.current = false; return; }
      active.current = true;
      startY.current = e.touches[0].clientY;
    };
    const onMove = (e) => {
      if (!active.current || refreshing) return;
      if (!atTop()) { active.current = false; setPull(0); return; }
      const dy = e.touches[0].clientY - startY.current;
      if (dy > 0) setPull(Math.min(MAX, dy * 0.5));
      else setPull(0);
    };
    const onEnd = () => {
      if (!active.current) return;
      active.current = false;
      const shouldRefresh = pull >= THRESHOLD;
      setPull(0);
      if (shouldRefresh) doRefresh();
    };
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTouch, ptrDisabled, pull, refreshing]);

  const ready = pull >= THRESHOLD;

  return (
    <>
      {isTouch && (pull > 0 || refreshing) && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-[65] pointer-events-none"
          style={{ top: Math.max(8, (refreshing ? 26 : pull) - 20) }}
          data-testid="ptr-indicator"
        >
          <div className="flex flex-col items-center gap-1">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-forest-700 text-white shadow-lg">
              <RefreshCw
                className={`w-5 h-5 ${refreshing || ready ? 'animate-spin' : ''}`}
                style={refreshing ? undefined : { transform: `rotate(${pull * 3}deg)` }}
              />
            </div>
            {!refreshing && (
              <span className="text-[11px] font-medium text-forest-800 bg-white/90 px-2 py-0.5 rounded-full shadow-sm">
                {ready ? 'Release to refresh' : 'Pull to refresh'}
              </span>
            )}
          </div>
        </div>
      )}

      {!isTouch && (
        <button
          onClick={doRefresh}
          title="Refresh & update"
          aria-label="Refresh and update"
          data-testid="desktop-refresh-btn"
          className="fixed bottom-5 left-5 z-[60] flex items-center justify-center w-11 h-11 rounded-full bg-forest-700 text-white shadow-2xl hover:bg-forest-800 active:scale-95 transition-colors"
        >
          <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      )}
    </>
  );
}
